import {Trans} from '@lingui/macro';
import React from 'react';

import {LocalApplicationFeaturesEnum} from '../../../models/api/localApplicationModel';
import {SettingsDevelopersPagesEnum, SettingsDevelopersUrl} from '../../../models/url/settingsUrlModel';

/*
 * Constants.
 */

export enum SettingsDevelopersInfoBoxTypesEnum {
  APPLICATIONS = 'APPLICATIONS',
  TOKENS = 'TOKENS',
  FEATURES = 'FEATURES',
  FEATURE_INBOX_SIDEPANEL = 'FEATURE_INBOX_SIDEPANEL',
  FEATURE_CHANNEL = 'FEATURE_CHANNEL',
  OAUTH = 'OAUTH',
  SERVERS = 'SERVERS',
}

export const settingsDevelopersInfoBoxKeys: {[key in SettingsDevelopersInfoBoxTypesEnum]: string} = {
  [SettingsDevelopersInfoBoxTypesEnum.APPLICATIONS]: `DEV-${SettingsDevelopersInfoBoxTypesEnum.APPLICATIONS}-infoBox`,
  [SettingsDevelopersInfoBoxTypesEnum.TOKENS]: `DEV-${SettingsDevelopersInfoBoxTypesEnum.TOKENS}-infoBox`,
  [SettingsDevelopersInfoBoxTypesEnum.FEATURES]: `DEV-${SettingsDevelopersInfoBoxTypesEnum.FEATURES}-infoBox`,
  [SettingsDevelopersInfoBoxTypesEnum.FEATURE_INBOX_SIDEPANEL]: `DEV-${SettingsDevelopersInfoBoxTypesEnum.FEATURE_INBOX_SIDEPANEL}-infoBox`,
  [SettingsDevelopersInfoBoxTypesEnum.FEATURE_CHANNEL]: `DEV-${SettingsDevelopersInfoBoxTypesEnum.FEATURE_CHANNEL}-infoBox`,
  [SettingsDevelopersInfoBoxTypesEnum.OAUTH]: `DEV-${SettingsDevelopersInfoBoxTypesEnum.OAUTH}-infoBox`,
  [SettingsDevelopersInfoBoxTypesEnum.SERVERS]: `DEV-${SettingsDevelopersInfoBoxTypesEnum.SERVERS}-infoBox`,
};

export const settingsDevelopersTitleRenderMap: {
  [key in SettingsDevelopersInfoBoxTypesEnum]: React.ReactNode;
} = {
  [SettingsDevelopersInfoBoxTypesEnum.APPLICATIONS]: (
    <Trans id="settings-developers-info-box-apps-title">Apps</Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.TOKENS]: (
    <Trans id="settings-developers-info-box-tokens-title">API tokens</Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.FEATURES]: (
    <Trans id="settings-developers-info-box-features-title">Features</Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.FEATURE_INBOX_SIDEPANEL]: (
    <Trans id="settings-developers-info-box-feature-inbox-sidepanel-title">Plugins</Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.FEATURE_CHANNEL]: (
    <Trans id="settings-developers-info-box-feature-channel-title">Channel type</Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.OAUTH]: (
    <Trans id="settings-developers-info-box-oauth-title">OAuth</Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.SERVERS]: (
    <Trans id="settings-developers-info-box-servers-title">Servers</Trans>
  ),
};

export const settingsDevelopersDescriptionRenderMap: {
  [key in SettingsDevelopersInfoBoxTypesEnum]: React.ReactNode;
} = {
  [SettingsDevelopersInfoBoxTypesEnum.APPLICATIONS]: (
    <Trans id="settings-developers-info-box-apps-description">
      Apps allow you to build custom functionality into Front, from embedded UI to new channels to workflow
      automations. Once you create an app, you can keep it private to your instance of Front or publish it to
      the App Store.
    </Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.TOKENS]: (
    <Trans id="settings-developers-info-box-tokens-description">
      API tokens allow you to make requests to Front&apos;s Core API to create, update, or delete almost
      anything in Front. You can sync contacts from a CRM, export analytics data, provision new teammates,
      automatically route conversations and more.
    </Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.FEATURES]: (
    <Trans id="settings-developers-info-box-features-description">
      Extend Front&apos;s native capabilities with custom UI embedded directly in Front or channels to connect
      an external messaging source. Most apps only need one feature, but you can also combine them in a single
      app.
    </Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.FEATURE_INBOX_SIDEPANEL]: (
    <Trans id="settings-developers-info-box-feature-inbox-sidepanel-description">
      Plugins allow you to embed a web app into Front&apos;s sidebar. Using the Plugin SDK, your app can
      interact with Front by automatically retrieving records for the user&apos;s current conversation,
      applying links or tags, or even creating message drafts.
    </Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.FEATURE_CHANNEL]: (
    <Trans id="settings-developers-info-box-feature-channel-description">
      A channel type defines how your channel should work in Front, from authentication to the type of
      contacts to the kind of message composer. Once your channel type is created, it&apos;s used as a
      template for each channel instance that a user creates. For example, when building an SMS channel, your
      channel type would set the contact type to phone, but you could use the type to connect multiple
      different phone numbers.
    </Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.OAUTH]: (
    <Trans id="settings-developers-info-box-oauth-description">
      OAuth allows users to securely grant your app access to their Front data, such as conversations or
      teammates or inboxes. Recommended for apps that need to fetch or update data in Front on behalf of a
      customer. For example, syncing contacts or routing conversations or exporting analytics data.
    </Trans>
  ),
  [SettingsDevelopersInfoBoxTypesEnum.SERVERS]: (
    <Trans id="settings-developers-info-box-servers-description">
      Use Servers to define how users should authorize your app. Handling authorization through a Front Server
      is currently only available for channel apps, but support for other kinds of apps is coming soon.
    </Trans>
  ),
};

/*
 * Helpers.
 */

export function computeInfoBoxKeyFromUrl(url: SettingsDevelopersUrl) {
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATIONS) {
    return settingsDevelopersInfoBoxKeys[SettingsDevelopersInfoBoxTypesEnum.APPLICATIONS];
  }
  if (url.subPage === SettingsDevelopersPagesEnum.TOKENS && !url.tokenId) {
    return settingsDevelopersInfoBoxKeys[SettingsDevelopersInfoBoxTypesEnum.TOKENS];
  }
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_FEATURES && !url.feature) {
    return settingsDevelopersInfoBoxKeys[SettingsDevelopersInfoBoxTypesEnum.FEATURES];
  }
  if (
    url.subPage === SettingsDevelopersPagesEnum.APPLICATION_FEATURES &&
    url.feature === LocalApplicationFeaturesEnum.INBOX_SIDEPANEL
  ) {
    return settingsDevelopersInfoBoxKeys[SettingsDevelopersInfoBoxTypesEnum.FEATURE_INBOX_SIDEPANEL];
  }
  if (
    url.subPage === SettingsDevelopersPagesEnum.APPLICATION_FEATURES &&
    url.feature === LocalApplicationFeaturesEnum.CHANNEL
  ) {
    return settingsDevelopersInfoBoxKeys[SettingsDevelopersInfoBoxTypesEnum.FEATURE_CHANNEL];
  }
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_OAUTH) {
    return settingsDevelopersInfoBoxKeys[SettingsDevelopersInfoBoxTypesEnum.OAUTH];
  }
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_SERVERS) {
    return settingsDevelopersInfoBoxKeys[SettingsDevelopersInfoBoxTypesEnum.SERVERS];
  }
  return undefined;
}

export function computeInfoBoxTypeFromUrl(url: SettingsDevelopersUrl) {
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATIONS) {
    return SettingsDevelopersInfoBoxTypesEnum.APPLICATIONS;
  }
  if (url.subPage === SettingsDevelopersPagesEnum.TOKENS && !url.tokenId) {
    return SettingsDevelopersInfoBoxTypesEnum.TOKENS;
  }
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_FEATURES && !url.feature) {
    return SettingsDevelopersInfoBoxTypesEnum.FEATURES;
  }
  if (
    url.subPage === SettingsDevelopersPagesEnum.APPLICATION_FEATURES &&
    url.feature === LocalApplicationFeaturesEnum.INBOX_SIDEPANEL
  ) {
    return SettingsDevelopersInfoBoxTypesEnum.FEATURE_INBOX_SIDEPANEL;
  }
  if (
    url.subPage === SettingsDevelopersPagesEnum.APPLICATION_FEATURES &&
    url.feature === LocalApplicationFeaturesEnum.CHANNEL
  ) {
    return SettingsDevelopersInfoBoxTypesEnum.FEATURE_CHANNEL;
  }
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_OAUTH) {
    return SettingsDevelopersInfoBoxTypesEnum.OAUTH;
  }
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_SERVERS) {
    return SettingsDevelopersInfoBoxTypesEnum.SERVERS;
  }
  return undefined;
}
